import { XML_LANGUAGE_ID } from '@lved/shared-core';
import prettierPluginXml from '@prettier/plugin-xml';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable } from '@theia/core/shared/inversify';
import type { TextFileContent } from '@theia/filesystem/lib/browser/file-service';
import { FileService } from '@theia/filesystem/lib/browser/file-service';
import * as monaco from '@theia/monaco-editor-core';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';
import type { Config, Options } from 'prettier';
import * as prettierPluginBabel from 'prettier/plugins/babel';
import * as prettierPluginEstree from 'prettier/plugins/estree';
import * as prettierPluginHtml from 'prettier/plugins/html';
import * as prettierPluginMarkdown from 'prettier/plugins/markdown';
import * as prettierPluginPostcss from 'prettier/plugins/postcss';
import * as prettierPluginTypescript from 'prettier/plugins/typescript';
import * as prettierPluginYaml from 'prettier/plugins/yaml';
import * as prettier from 'prettier/standalone';

type SupportedParser = Exclude<Options['parser'], undefined>;

export const PRETTIER_FORMATTER_ID: string = 'lvgl.prettier';
export const SUPPORTED_PRETTIER_LANGUAGE_IDS: string[] = [XML_LANGUAGE_ID, 'markdown', 'yaml'] as const;

const PRETTIER_CONFIG_FILES: string[] = ['.prettierrc', '.prettierrc.json'];
const PRETTIER_PLUGINS: Options['plugins'] = [
    prettierPluginBabel,
    prettierPluginEstree,
    prettierPluginHtml,
    prettierPluginMarkdown,
    prettierPluginPostcss,
    prettierPluginTypescript,
    prettierPluginYaml,
    prettierPluginXml
];
const XML_PRETTIER_OPTIONS: Options = {
    parser: XML_LANGUAGE_ID,
    endOfLine: 'lf',
    useTabs: true,
    proseWrap: 'always',
    bracketSameLine: false,
    printWidth: 120,
    singleAttributePerLine: false,
    tabWidth: 4,
    xmlQuoteAttributes: 'double',
    xmlSelfClosingSpace: true,
    xmlSortAttributesByKey: false,
    xmlWhitespaceSensitivity: 'ignore',
    xmlExpandSelfClosing: true
} as const;

const parserByLanguageId: ReadonlyMap<string, SupportedParser> = new Map<string, SupportedParser>([
    [XML_LANGUAGE_ID, XML_LANGUAGE_ID],
    ['markdown', 'markdown'],
    ['yaml', 'yaml']
]);

type FormatterMetadata = {
    extensionId: { value: string };
    displayName: string;
};

@injectable()
export class PrettierFormattingContribution implements FrontendApplicationContribution {
    @inject(FileService)
    protected readonly fileService!: FileService;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    protected readonly toDispose = new DisposableCollection();
    protected readonly configCache = new Map<string, Options>();

    initialize(): void {
        for (const languageId of SUPPORTED_PRETTIER_LANGUAGE_IDS) {
            this.toDispose.push(
                monaco.languages.registerDocumentFormattingEditProvider(languageId, this.createProvider())
            );
        }
    }

    protected createProvider(): monaco.languages.DocumentFormattingEditProvider {
        const provider: monaco.languages.DocumentFormattingEditProvider & FormatterMetadata = {
            extensionId: { value: PRETTIER_FORMATTER_ID },
            displayName: 'Prettier',
            provideDocumentFormattingEdits: async (model, options, token) => {
                if (token.isCancellationRequested) return [];

                const parser: SupportedParser | undefined = parserByLanguageId.get(model.getLanguageId());
                if (!parser) return [];

                try {
                    const configuredOptions: Options = await this.getConfiguredOptions(model);
                    if (token.isCancellationRequested) return [];

                    const languageFormattingOptions: Partial<Options> = this.getLanguageFormattingOptions(
                        model,
                        options
                    );

                    const formatted: string = await prettier.format(model.getValue(), {
                        ...configuredOptions,
                        ...languageFormattingOptions,
                        parser,
                        filepath: this.getFilePath(model),
                        plugins: PRETTIER_PLUGINS
                    });

                    if (formatted === model.getValue()) return [];

                    return [
                        {
                            range: model.getFullModelRange(),
                            text: formatted
                        }
                    ];
                } catch {
                    return [];
                }
            }
        };

        return provider;
    }

    protected async getConfiguredOptions(model: monaco.editor.ITextModel): Promise<Options> {
        const resourceUri: URI = new URI(model.uri.toString());
        const workspaceRoot: URI | undefined = this.workspaceService.getWorkspaceRootUri(resourceUri);
        if (!workspaceRoot) return this.getDefaultConfiguredOptions(model);

        const workspaceConfig: Options | undefined = await this.getWorkspaceConfig(workspaceRoot);
        return workspaceConfig ?? this.getDefaultConfiguredOptions(model);
    }

    protected async getWorkspaceConfig(workspaceRoot: URI): Promise<Options | undefined> {
        const cacheKey: string = workspaceRoot.toString();
        const cached: Options | undefined = this.configCache.get(cacheKey);
        if (cached) return cached;

        for (const configFile of PRETTIER_CONFIG_FILES) {
            try {
                const file: TextFileContent = await this.fileService.read(workspaceRoot.resolve(configFile));
                const config: Config = JSON.parse(file.value);
                this.configCache.set(cacheKey, config);
                return config;
            } catch {
                continue;
            }
        }
    }

    protected getFilePath(model: monaco.editor.ITextModel): string {
        return model.uri.scheme === 'file' ? model.uri.fsPath : model.uri.path;
    }

    protected getDefaultConfiguredOptions(model: monaco.editor.ITextModel): Options {
        if (model.getLanguageId() === XML_LANGUAGE_ID) return XML_PRETTIER_OPTIONS;
        return {};
    }

    protected getLanguageFormattingOptions(
        model: monaco.editor.ITextModel,
        options: monaco.languages.FormattingOptions
    ): Partial<Options> {
        if (model.getLanguageId() === XML_LANGUAGE_ID) return {};

        return {
            tabWidth: options.tabSize,
            useTabs: !options.insertSpaces,
            endOfLine: this.toEndOfLine(model.getEOL())
        };
    }

    protected toEndOfLine(value: string): Options['endOfLine'] {
        return value === '\r\n' ? 'crlf' : value === '\r' ? 'cr' : 'lf';
    }
}
